import { useState } from "react";
import { Link } from "react-router-dom";
import PermissionNotice from "@/components/PermissionNotice";
import { useAuth } from "@/hooks/useAuth";
import { useCaseList, useCreateCase, useDeleteCase } from "@/hooks/useCases";
import type { CaseCreate, CaseType } from "@/types";

const statusLabels: Record<string, string> = {
  open: "접수",
  in_progress: "진행중",
  pending: "보류",
  closed: "종결",
};

const caseTypeLabels: Record<string, string> = {
  fire: "화재",
  arson: "방화",
  terror: "테러",
  leak: "가스누출",
  death: "사망",
  other: "기타",
};

const statusColor = (status: string) => {
  switch (status) {
    case "open":
      return "text-blue-700 bg-blue-100";
    case "in_progress":
      return "text-yellow-700 bg-yellow-100";
    case "closed":
      return "text-green-700 bg-green-100";
    default:
      return "text-gray-700 bg-gray-100";
  }
};

const INITIAL_FORM: CaseCreate = {
  case_number: "",
  title: "",
  description: "",
  case_type: "fire",
  location: "",
};

export default function CasesPage() {
  const { permissions } = useAuth();
  const { data: cases, isLoading, error } = useCaseList();
  const createCase = useCreateCase();
  const deleteCase = useDeleteCase();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CaseCreate>(INITIAL_FORM);

  const handleCreate = async () => {
    if (!form.case_number.trim() || !form.title.trim()) {
      alert("사건번호와 제목은 필수 입력 항목입니다.");
      return;
    }
    try {
      await createCase.mutateAsync(form);
      setForm(INITIAL_FORM);
      setShowForm(false);
    } catch {
      alert("사건 등록에 실패했습니다.");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("정말 이 사건을 삭제하시겠습니까?")) return;
    try {
      await deleteCase.mutateAsync(id);
    } catch {
      alert("삭제에 실패했습니다.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">사건 관리</h1>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">사건 관리</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">사건 관리</h1>
        <p className="text-gray-600">사건을 등록하고 관리할 수 있습니다.</p>
      </div>

      {/* 새 사건 등록 */}
      {permissions?.canCreateCases ? (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            신규 사건 등록
          </h2>
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              + 새 사건 등록
            </button>
          )}
        </div>

        {showForm ? (
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    사건번호 *
                  </span>
                  <input
                    type="text"
                    value={form.case_number}
                    onChange={(e) =>
                      setForm({ ...form, case_number: e.target.value })
                    }
                    placeholder="예: 2025-FIRE-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    제목 *
                  </span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    placeholder="사건 제목"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    사건 유형
                  </span>
                  <select
                    value={form.case_type}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        case_type: e.target.value as CaseType,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="fire">화재</option>
                    <option value="arson">방화</option>
                    <option value="terror">테러</option>
                    <option value="leak">가스누출</option>
                    <option value="death">사망</option>
                    <option value="other">기타</option>
                  </select>
                </label>
              </div>
              <div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    발생 장소
                  </span>
                  <input
                    type="text"
                    value={form.location ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, location: e.target.value })
                    }
                    placeholder="장소"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    설명
                  </span>
                  <textarea
                    value={form.description ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    rows={3}
                    placeholder="사건에 대한 상세 설명"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setForm(INITIAL_FORM);
                }}
                disabled={createCase.isPending}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={createCase.isPending}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createCase.isPending ? "등록 중..." : "사건 등록"}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            새로운 사건을 등록하려면 "새 사건 등록" 버튼을 클릭하세요.
          </div>
        )}
      </div>
      ) : (
        <PermissionNotice action="사건 등록" requiredRole="수사관" />
      )}

      {/* 사건 목록 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">사건 목록</h2>
          <p className="text-sm text-gray-500 mt-1">
            총 {cases?.length ?? 0}개의 사건
          </p>
        </div>

        {cases && cases.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사건번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    등록일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cases.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/cases/${c.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {c.case_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Link
                        to={`/cases/${c.id}`}
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        {c.title}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {caseTypeLabels[c.case_type] ?? c.case_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColor(c.status)}`}
                      >
                        {statusLabels[c.status] ?? c.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(c.created_at).toLocaleDateString("ko-KR")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {permissions?.canDeleteCases && (
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        disabled={deleteCase.isPending}
                        className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50 cursor-pointer"
                      >
                        삭제
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              등록된 사건이 없습니다
            </p>
            <p className="text-gray-500">
              위의 "새 사건 등록" 버튼으로 사건을 추가하세요.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
